import { Controller } from "@hotwired/stimulus"
import { post, patch } from "@rails/request.js"

// Drives the login-time IMO/Agency confirmation modal (Phase 1).
// - "Yes, this is correct" -> POST confirm-url (stamps confirmed_at), close.
// - "Update" -> reveal edit fields; "Save" -> PATCH update-url through the
//   ImoAgencyResolver, close on success or show the returned error.
// The modal element also carries the `auto-modal` controller which opens it.
export default class extends Controller {
  static targets = [
    "summary", "edit", "error", "imoInput", "agencyInput",
    "imoConfirmedNewInput", "agencyConfirmedNewInput",
    "imoGateWrapper", "agencyGateWrapper",
  ]
  static values = {
    confirmUrl: String,
    updateUrl: String,
    initialImoName: String,
    initialImoId: String,
  }

  // Both states span two elements each (body block + footer action row), so
  // toggle every tagged element, not just the first.
  showEdit() {
    this.hideError()
    this.summaryTargets.forEach((el) => el.classList.add("d-none"))
    this.editTargets.forEach((el) => el.classList.remove("d-none"))
  }

  cancelEdit() {
    this.hideError()
    this.editTargets.forEach((el) => el.classList.add("d-none"))
    this.summaryTargets.forEach((el) => el.classList.remove("d-none"))
  }

  async confirm() {
    const response = await post(this.confirmUrlValue, { responseKind: "json" })
    if (response.ok) {
      this.close()
    } else {
      this.showError("Something went wrong. Please try again.")
    }
  }

  // Handles "org-suggestion:fix-misclassification" (Phase 3c): the agent typed a
  // known agency name into the IMO field, and picked one of its real parent IMOs.
  // Set IMO to the chosen parent, Agency to what they typed, and save straight
  // through — the resolver nests the agency correctly under the IMO.
  applyMisclassification(event) {
    const { imoName, agencyName } = event.detail || {}
    if (!imoName || !agencyName) return
    this.imoInputTarget.value = imoName
    this.agencyInputTarget.value = agencyName
    this.imoChanged()
    this.submit()
  }

  // Keeps the agency field's org-confirm-gate parent scoping honest when the
  // IMO text changes on THIS surface. The onboarding/profile surfaces have the
  // imo-agency controller re-stamping the same attribute (syncAgencySuggesterParent);
  // this modal hosts imo-agency-confirmation instead, and its IMO field is
  // plain free-text with no client-side name->id option list. So: typed value
  // still equals the rendered IMO -> restore its id; anything else -> clear the
  // scope entirely. An UNSCOPED agency lookup deliberately returns no fuzzy
  // matches (SimilarOrgSuggester#unscopable_agency?), which turns "wrong
  // suggestions under a stale parent" into "no suggestions" — the server-side
  // resolver re-scopes authoritatively against the freshly-typed IMO on Save.
  imoChanged() {
    // A "create new agency" confirmation is only meaningful against the IMO it
    // was confirmed under — a stale agency_confirmed_new=true surviving an IMO
    // edit would let Save skip the server gate's suggestion check entirely
    // under the NEW IMO. (Onboarding/profile get this for free: imo-agency's
    // syncAgencies clears the agency value on IMO change, which fires input ->
    // resetConfirmed. This surface has no imo-agency controller, so reset here.)
    if (this.hasAgencyConfirmedNewInputTarget) this.agencyConfirmedNewInputTarget.value = ""

    if (!this.hasAgencyGateWrapperTarget) return

    const typed = this.imoInputTarget.value.trim().toLowerCase()
    const initial = (this.initialImoNameValue || "").trim().toLowerCase()
    const stillInitial = typed !== "" && typed === initial
    this.agencyGateWrapperTarget.dataset.orgConfirmGateParentIdValue =
      stillInitial ? this.initialImoIdValue : ""
  }

  async submit() {
    const body = new FormData()
    body.append("user[imo]", this.imoInputTarget.value)
    body.append("user[agency]", this.agencyInputTarget.value)
    // TOP-LEVEL, not nested under user[...] — update_imo_agency reads these via
    // top_level_confirmed_new?(key) (params[key]), unlike the profile form's
    // resolve_imo_and_agency! which reads params.dig(:user, key). Set by the
    // org-confirm-gate controller wired on each field (Phase 4 forced-choice gate).
    if (this.hasImoConfirmedNewInputTarget) body.append("imo_confirmed_new", this.imoConfirmedNewInputTarget.value)
    if (this.hasAgencyConfirmedNewInputTarget) body.append("agency_confirmed_new", this.agencyConfirmedNewInputTarget.value)

    const response = await patch(this.updateUrlValue, { body, responseKind: "json" })
    if (response.ok) {
      this.close()
      return
    }

    let message = "Please check your IMO and Agency and try again."
    let confirmation = null
    try {
      const data = await response.json
      if (data && data.error) message = data.error
      if (data && data.org_confirmation) confirmation = data.org_confirmation
    } catch {
      // keep the default message
    }
    this.showError(message)
    // Server-side gate refusal: open the forced-choice modal on the refused
    // field (same experience as the commit-time check the Save raced past).
    if (confirmation) this.openGateModal(confirmation)
  }

  // Route the 422 body's gate payload to the refused field's org-confirm-gate
  // controller by dispatching on its wrapper (each wrapper wires
  // org-confirm-gate:open->org-confirm-gate#openFromEvent).
  openGateModal(confirmation) {
    const wrapper = confirmation.field === "agency"
      ? (this.hasAgencyGateWrapperTarget && this.agencyGateWrapperTarget)
      : (this.hasImoGateWrapperTarget && this.imoGateWrapperTarget)
    if (!wrapper) return

    wrapper.dispatchEvent(new CustomEvent("org-confirm-gate:open", { detail: confirmation }))
  }

  close() {
    const Modal = window.bootstrap?.Modal
    if (!Modal) return
    const instance = Modal.getInstance(this.element) || Modal.getOrCreateInstance(this.element)
    instance?.hide()
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  hideError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }
}
